import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Camera, Paperclip, Plus, Trash2, X } from 'lucide-react';
import {
  createClaim,
  getClaimReference,
  submitClaim,
  uploadScanFile,
  type ClaimLineItem,
  type ClaimReference,
  type EmployeeProfile,
  type MileageMeta,
} from '../api/claims';
import DocumentScanner from '../components/DocumentScanner';
import PageHeader from '../components/ui/PageHeader';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { cn } from '@/lib/utils';

// ── Generic invoice line ────────────────────────────────────────────────────
const emptyLine = (): ClaimLineItem => ({ invoiceNo: '', description: '', amount: 0, quantity: 1 });

// ── Mileage trip row ────────────────────────────────────────────────────────
interface TripRow {
  date: string;
  destination: string;
  purposeOfTravel: string;
  miles: number;
  tollParking: number;
  subsistenceAmount: number;
}

const emptyTrip = (): TripRow => ({
  date: new Date().toISOString().split('T')[0],
  destination: '',
  purposeOfTravel: '',
  miles: 0,
  tollParking: 0,
  subsistenceAmount: 0,
});

// ── Form A, mode B: itemized subsistence purchase ──────────────────────────
interface SubsistenceItemRow {
  description: string;
  unit: number;
  ratePerUnit: number;
}

const emptySubsistenceItem = (): SubsistenceItemRow => ({ description: '', unit: 1, ratePerUnit: 0 });

// ── Subsistence staff row ───────────────────────────────────────────────────
interface SubsistenceRow {
  staffName: string;
  dayOfTravel: string;
  breakfast: number;
  lunch: number;
  dinner: number;
  mileageAmount: number;
  entranceFee: number;
}

const emptySubRow = (name = ''): SubsistenceRow => ({
  staffName: name,
  dayOfTravel: '',
  breakfast: 0,
  lunch: 0,
  dinner: 0,
  mileageAmount: 0,
  entranceFee: 0,
});

export default function ClaimNew() {
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [ref, setRef] = useState<ClaimReference | null>(null);
  const [companyEntityId, setCompanyEntityId] = useState('');
  const [claimType, setClaimType] = useState('CLAIM');
  const [purpose, setPurpose] = useState('');
  const [expenseDate, setExpenseDate] = useState(new Date().toISOString().split('T')[0]);
  const [bankPayableTo, setBankPayableTo] = useState('');
  const [bankName, setBankName] = useState('');
  const [bankAccountNo, setBankAccountNo] = useState('');
  const [documents, setDocuments] = useState<string[]>([]);
  const [additionalDetails, setAdditionalDetails] = useState('');
  const [lines, setLines] = useState<ClaimLineItem[]>([emptyLine()]);
  const [claimAmount, setClaimAmount] = useState('');

  // MILEAGE-specific — reached via a toggle under the Claim Form, not its own Form type entry
  const [mileageMode, setMileageMode] = useState(false);
  const [formAMode, setFormAMode] = useState<'mileage' | 'subsistence'>('mileage');
  const [vehicleType, setVehicleType] = useState<'car' | 'motor'>('car');
  const [plateNo, setPlateNo] = useState('');
  const [trips, setTrips] = useState<TripRow[]>([emptyTrip()]);
  const [subsistenceItems, setSubsistenceItems] = useState<SubsistenceItemRow[]>([emptySubsistenceItem()]);

  // SUBSISTENCE-specific
  const [subRows, setSubRows] = useState<SubsistenceRow[]>([emptySubRow()]);
  const [destination, setDestination] = useState('');

  const [proofFiles, setProofFiles] = useState<{ name: string; url: string; path?: string }[]>([]);
  const [scannerOpen, setScannerOpen] = useState(false);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [pendingScanUrl, setPendingScanUrl] = useState<string | null>(null);
  const [attachingScan, setAttachingScan] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    getClaimReference().then((res) => {
      if (res.data?.data) {
        const data = res.data.data;
        setRef(data);
        setCompanyEntityId(data.employeeCompanyEntityId ?? data.defaultCompanyEntityId ?? '');
        const first = Object.keys(data.formTypes)[0];
        if (first) setClaimType(first);
        // Auto-fill bank details and staff name from employee profile
        const p = data.employeeProfile;
        if (p) {
          if (p.bankAccountName) setBankPayableTo(p.bankAccountName);
          if (p.bankAccountNumber) setBankAccountNo(p.bankAccountNumber);
          if (p.fullName) setSubRows([emptySubRow(p.fullName)]);
        }
      }
    });
  }, []);

  const formTypes = useMemo(() => {
    if (!ref) return [];
    // MILEAGE is reached via a toggle under the Claim Form, not a Form type of its own.
    return Object.entries(ref.formTypes)
      .filter(([code]) => code !== 'MILEAGE')
      .map(([code, meta]) => ({
        code,
        label: meta.label,
        description: meta.description,
      }));
  }, [ref]);

  const profile: EmployeeProfile | null = ref?.employeeProfile ?? null;
  const isMileage = claimType === 'CLAIM' && mileageMode;
  const isSubsistence = claimType === 'TRAVELLING_SUBSISTENCE';
  const usesDirectClaimAmount = claimType === 'CLAIM';
  const rate = ref?.mileageRates?.[vehicleType] ?? (vehicleType === 'car' ? 1.00 : 0.60);

  // Total calculations
  const genericTotal = useMemo(
    () => lines.reduce((s, l) => s + Number(l.amount || 0) * (l.quantity ?? 1), 0),
    [lines],
  );

  const mileageTotal = useMemo(
    () => trips.reduce((s, t) => {
      const a = t.miles * rate;
      return s + a + t.tollParking + t.subsistenceAmount;
    }, 0),
    [trips, rate],
  );

  const subsistenceItemsTotal = useMemo(
    () => subsistenceItems.reduce((s, r) => s + r.unit * r.ratePerUnit, 0),
    [subsistenceItems],
  );

  const subsistenceTotal = useMemo(
    () => subRows.reduce((s, r) => s + r.breakfast + r.lunch + r.dinner + r.mileageAmount + r.entranceFee, 0),
    [subRows],
  );

  const totalAmount = isMileage
    ? (formAMode === 'mileage' ? mileageTotal : subsistenceItemsTotal)
    : isSubsistence
      ? subsistenceTotal
      : usesDirectClaimAmount
        ? Number(claimAmount) || 0
        : genericTotal;

  // Line helpers
  const updateLine = (i: number, patch: Partial<ClaimLineItem>) =>
    setLines((prev) => prev.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));

  const updateTrip = (i: number, patch: Partial<TripRow>) =>
    setTrips((prev) => prev.map((t, idx) => (idx === i ? { ...t, ...patch } : t)));

  const updateSubsistenceItem = (i: number, patch: Partial<SubsistenceItemRow>) =>
    setSubsistenceItems((prev) => prev.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));

  const updateSubRow = (i: number, patch: Partial<SubsistenceRow>) =>
    setSubRows((prev) => prev.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));

  const toggleDocument = (code: string) =>
    setDocuments((prev) => prev.includes(code) ? prev.filter((d) => d !== code) : [...prev, code]);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    if (!files.length) return;
    setUploadingFiles(true);
    for (const file of files) {
      try {
        const dataUrl = await readFileAsDataUrl(file);
        const { path, url } = await uploadScanFile(dataUrl);
        setProofFiles((prev) => [...prev, { name: file.name, url, path }]);
      } catch {
        setError('Failed to upload file. Please try again.');
      }
    }
    setUploadingFiles(false);
    if (e.target) e.target.value = '';
  };

  const handleScanCapture = (dataUrl: string) => {
    setScannerOpen(false);
    setPendingScanUrl(dataUrl);
  };

  const handleAttachScan = async () => {
    if (!pendingScanUrl) return;
    setAttachingScan(true);
    try {
      const { path, url } = await uploadScanFile(pendingScanUrl);
      setProofFiles((prev) => [...prev, { name: `scan_${Date.now()}.jpg`, url, path }]);
      setPendingScanUrl(null);
    } catch {
      setError('Failed to upload scanned image.');
    }
    setAttachingScan(false);
  };

  const buildClaimBody = () => {
    if (isMileage && formAMode === 'subsistence') {
      // No mileage meta on these items — the backend falls back to amount × quantity per item.
      const items: ClaimLineItem[] = subsistenceItems
        .filter((r) => r.description.trim() && r.unit > 0)
        .map((r) => ({
          description: r.description,
          amount: r.ratePerUnit,
          quantity: r.unit,
        }));
      return {
        claimType: 'MILEAGE',
        title: purpose || 'Subsistence claim',
        expenseDate,
        supportingFiles: proofFiles.map((f) => f.path).filter(Boolean) as string[],
        items: items.length > 0 ? items : undefined,
        amount: items.length === 0 ? totalAmount || undefined : undefined,
      };
    }

    if (isMileage) {
      const items: ClaimLineItem[] = trips.filter((t) => t.destination || t.miles > 0).map((t) => {
        const totalMileage = t.miles * rate;
        const meta: MileageMeta = {
          date: t.date,
          destination: t.destination,
          purposeOfTravel: t.purposeOfTravel,
          miles: t.miles,
          rate,
          totalMileage,
          tollParking: t.tollParking,
          subsistenceAmount: t.subsistenceAmount,
        };
        return {
          description: t.destination || t.purposeOfTravel || 'Trip',
          amount: rate,
          quantity: 1,
          meta,
        };
      });
      return {
        claimType: 'MILEAGE',
        title: purpose || 'Mileage claim',
        expenseDate,
        vehicleType,
        plateNo: plateNo || undefined,
        projectName: undefined,
        supportingFiles: proofFiles.map((f) => f.path).filter(Boolean) as string[],
        items: items.length > 0 ? items : undefined,
        amount: items.length === 0 ? totalAmount || undefined : undefined,
      };
    }

    if (isSubsistence) {
      const items: ClaimLineItem[] = subRows.filter((r) => r.staffName || r.dayOfTravel).map((r) => ({
        description: r.staffName || 'Staff',
        amount: r.breakfast + r.lunch + r.dinner + r.mileageAmount + r.entranceFee,
        quantity: 1,
        meta: {
          staffName: r.staffName,
          dayOfTravel: r.dayOfTravel,
          breakfast: r.breakfast,
          lunch: r.lunch,
          dinner: r.dinner,
          mileageAmount: r.mileageAmount,
          entranceFee: r.entranceFee,
        } as any,
      }));
      return {
        claimType,
        title: purpose || 'Travelling / Subsistence',
        expenseDate,
        clientName: destination || undefined,
        additionalDetails: additionalDetails || undefined,
        supportingFiles: proofFiles.map((f) => f.path).filter(Boolean) as string[],
        items: items.length > 0 ? items : undefined,
        amount: items.length === 0 ? totalAmount || undefined : undefined,
      };
    }

    // Generic types
    if (usesDirectClaimAmount) {
      return {
        claimType,
        title: purpose,
        expenseDate,
        bankPayableTo: bankPayableTo || undefined,
        bankName: bankName || undefined,
        bankAccountNo: bankAccountNo || undefined,
        documentsAttached: documents.length ? documents : undefined,
        additionalDetails: additionalDetails || undefined,
        amount: Number(claimAmount),
        supportingFiles: proofFiles.map((f) => f.path).filter(Boolean) as string[],
      };
    }

    const validLines = lines.filter((l) => l.description.trim() && Number(l.amount) > 0);
    return {
      claimType,
      title: purpose,
      expenseDate,
      bankPayableTo: bankPayableTo || undefined,
      bankName: bankName || undefined,
      bankAccountNo: bankAccountNo || undefined,
      documentsAttached: documents.length ? documents : undefined,
      additionalDetails: additionalDetails || undefined,
      items: validLines.length > 0 ? validLines : undefined,
      amount: validLines.length === 0 ? totalAmount || undefined : undefined,
      supportingFiles: proofFiles.map((f) => f.path).filter(Boolean) as string[],
    };
  };

  const handleSaveDraft = async () => {
    setLoading(true);
    setError('');
    const res = await createClaim({ ...buildClaimBody(), companyEntityId, submit: false });
    setLoading(false);
    if (res.error) { setError(res.error); return; }
    navigate('/claims', { replace: true });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    const res = await createClaim({ ...buildClaimBody(), companyEntityId, submit: false });
    if (res.error) { setLoading(false); setError(res.error); return; }
    const id = res.data?.data?.id;
    if (id) {
      const submitRes = await submitClaim(id);
      if (submitRes.error) { setLoading(false); setError(submitRes.error); return; }
    }
    setLoading(false);
    navigate('/claims', { replace: true });
  };

  const fieldClass =
    'flex h-11 w-full rounded-xl border border-input bg-background px-4 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';

  const hasValidAmount = totalAmount > 0 && Number.isFinite(totalAmount);
  const hasValidFormAEntry = isMileage
    ? (formAMode === 'mileage'
      ? trips.some((t) => t.destination || t.miles > 0)
      : subsistenceItems.some((r) => r.description.trim() && r.unit > 0))
    : !!purpose.trim();
  const canSubmit = hasValidFormAEntry
    && hasValidAmount
    && !!companyEntityId && !loading && !uploadingFiles;

  return (
    <>
      {scannerOpen && (
        <DocumentScanner onCapture={handleScanCapture} onClose={() => setScannerOpen(false)} />
      )}

      {pendingScanUrl && (
        <div className="fixed inset-0 z-[150] flex flex-col justify-end bg-black/60 backdrop-blur-sm">
          <div className="rounded-t-2xl bg-background overflow-hidden shadow-2xl">
            <div className="flex items-center justify-center bg-foreground" style={{ minHeight: '50vh' }}>
              <img src={pendingScanUrl} alt="Scanned document preview" className="max-h-[50vh] max-w-full object-contain" />
            </div>
            <div className="p-5 space-y-3">
              <p className="text-sm font-semibold text-center">Attach this scan?</p>
              <div className="flex gap-3">
                <Button type="button" variant="outline" className="flex-1" onClick={() => setPendingScanUrl(null)} disabled={attachingScan}>Discard</Button>
                <Button type="button" className="flex-1" onClick={handleAttachScan} disabled={attachingScan}>
                  {attachingScan ? 'Uploading…' : 'Attach scan'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="mx-auto max-w-lg space-y-5 pb-8">
        <PageHeader title="New claim" subtitle="Expense & requisition forms" />

        {error && (
          <div className="rounded-xl border border-destructive/20 bg-destructive/10 px-4 py-3 text-sm text-destructive">{error}</div>
        )}

        {/* Employee info header — auto-filled, read-only */}
        {profile && (
          <Card className="border-primary/20 bg-primary/5">
            <CardContent className="p-4">
              <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-primary">Claimant information</p>
              <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                <div>
                  <p className="text-xs text-muted-foreground">Full name</p>
                  <p className="font-medium">{profile.fullName}</p>
                </div>
                {profile.staffId && (
                  <div>
                    <p className="text-xs text-muted-foreground">Staff ID</p>
                    <p className="font-medium">{profile.staffId}</p>
                  </div>
                )}
                {profile.designation && (
                  <div>
                    <p className="text-xs text-muted-foreground">Designation</p>
                    <p className="font-medium">{profile.designation}</p>
                  </div>
                )}
                {profile.department && (
                  <div>
                    <p className="text-xs text-muted-foreground">Department</p>
                    <p className="font-medium">{profile.department}</p>
                  </div>
                )}
                {ref && ref.companies.length > 0 && (
                  <div className="col-span-2">
                    <label className="block">
                      <span className="mb-1 block text-xs text-muted-foreground">Company (legal entity)</span>
                      <select
                        value={companyEntityId}
                        onChange={(e) => setCompanyEntityId(e.target.value)}
                        className={fieldClass}
                        required
                      >
                        {ref.companies.map((c) => (
                          <option key={c.id} value={c.id}>{c.name}</option>
                        ))}
                      </select>
                    </label>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {ref && ref.companies.length === 0 && (
          <div className="rounded-xl border border-warning/20 bg-warning/10 px-4 py-3 text-sm text-warning">
            No legal entity is configured. Contact HR before submitting.
          </div>
        )}

        {/* Form type */}
        <label className="block">
          <span className="mb-1.5 block text-sm font-medium">Form type</span>
          <select
            value={claimType}
            onChange={(e) => { setClaimType(e.target.value); if (e.target.value !== 'CLAIM') setMileageMode(false); }}
            className={fieldClass}
            required
          >
            {formTypes.map((t) => <option key={t.code} value={t.code}>{t.label}</option>)}
          </select>
          {formTypes.find((t) => t.code === claimType)?.description && (
            <p className="mt-1 text-xs text-muted-foreground">{formTypes.find((t) => t.code === claimType)?.description}</p>
          )}
        </label>

        {claimType === 'CLAIM' && (
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={mileageMode} onChange={(e) => setMileageMode(e.target.checked)} className="rounded border-input text-primary focus:ring-ring" />
            This is a mileage / vehicle claim
          </label>
        )}

        {/* ── FORM A (Mileage / Subsistence) ─────────────────────────── */}
        {isMileage && (
          <div className="flex gap-2 rounded-2xl border border-border bg-muted/30 p-1">
            <button
              type="button"
              onClick={() => setFormAMode('mileage')}
              className={cn(
                'flex-1 rounded-xl px-3 py-2 text-sm font-medium transition-all',
                formAMode === 'mileage' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
              )}
            >
              A) Mileage
            </button>
            <button
              type="button"
              onClick={() => setFormAMode('subsistence')}
              className={cn(
                'flex-1 rounded-xl px-3 py-2 text-sm font-medium transition-all',
                formAMode === 'subsistence' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
              )}
            >
              B) Subsistence
            </button>
          </div>
        )}

        {isMileage && formAMode === 'mileage' && (
          <>
            <Card><CardContent className="space-y-3 p-4">
              <p className="text-sm font-medium">Vehicle details</p>
              <div className="flex gap-3">
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="radio" name="vehicleType" value="car" checked={vehicleType === 'car'} onChange={() => setVehicleType('car')} />
                  Car (RM {(ref?.mileageRates?.car ?? 1.00).toFixed(2)}/km)
                </label>
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="radio" name="vehicleType" value="motor" checked={vehicleType === 'motor'} onChange={() => setVehicleType('motor')} />
                  Motorcycle (RM {(ref?.mileageRates?.motor ?? 0.60).toFixed(2)}/km)
                </label>
              </div>
              <input type="text" placeholder="Plate no. (e.g. WQQ 1234)" value={plateNo} onChange={(e) => setPlateNo(e.target.value)} className={fieldClass} />
              <label className="block">
                <span className="mb-1 block text-xs text-muted-foreground">Month / Year</span>
                <input type="date" value={expenseDate} onChange={(e) => setExpenseDate(e.target.value)} className={fieldClass} />
              </label>
            </CardContent></Card>

            <Card><CardContent className="space-y-4 p-4">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium">Trip entries</p>
                <button type="button" onClick={() => setTrips((p) => [...p, emptyTrip()])} className="flex items-center gap-1 text-xs text-primary hover:underline">
                  <Plus className="h-3.5 w-3.5" /> Add trip
                </button>
              </div>

              {trips.map((trip, i) => {
                const mileA = trip.miles * rate;
                const rowTotal = mileA + trip.tollParking + trip.subsistenceAmount;
                return (
                  <div key={i} className="space-y-2 rounded-xl border border-border p-3">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-semibold text-muted-foreground">Trip {i + 1}</span>
                      {trips.length > 1 && (
                        <button type="button" onClick={() => setTrips((p) => p.filter((_, j) => j !== i))} className="text-destructive/70 hover:text-destructive">
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </div>
                    <input type="date" value={trip.date} onChange={(e) => updateTrip(i, { date: e.target.value })} className={fieldClass} />
                    <input type="text" placeholder="Destination" value={trip.destination} onChange={(e) => updateTrip(i, { destination: e.target.value })} className={fieldClass} />
                    <input type="text" placeholder="Purpose of travel" value={trip.purposeOfTravel} onChange={(e) => updateTrip(i, { purposeOfTravel: e.target.value })} className={fieldClass} />
                    <div className="grid grid-cols-3 gap-2">
                      <label className="block">
                        <span className="mb-1 block text-xs text-muted-foreground">Miles (KM)</span>
                        <input type="number" step="0.1" min="0" value={trip.miles || ''} onChange={(e) => updateTrip(i, { miles: parseFloat(e.target.value) || 0 })} className={fieldClass} placeholder="0" />
                      </label>
                      <label className="block">
                        <span className="mb-1 block text-xs text-muted-foreground">Toll / Parking (RM)</span>
                        <input type="number" step="0.01" min="0" value={trip.tollParking || ''} onChange={(e) => updateTrip(i, { tollParking: parseFloat(e.target.value) || 0 })} className={fieldClass} placeholder="0.00" />
                      </label>
                      <label className="block">
                        <span className="mb-1 block text-xs text-muted-foreground">Subsistence (RM)</span>
                        <input type="number" step="0.01" min="0" value={trip.subsistenceAmount || ''} onChange={(e) => updateTrip(i, { subsistenceAmount: parseFloat(e.target.value) || 0 })} className={fieldClass} placeholder="0.00" />
                      </label>
                    </div>
                    <div className="rounded-lg bg-muted/50 px-3 py-2 text-xs text-muted-foreground">
                      Mileage: RM {mileA.toFixed(2)} &nbsp;+&nbsp; Toll/Parking: RM {trip.tollParking.toFixed(2)} &nbsp;+&nbsp; Subsistence: RM {trip.subsistenceAmount.toFixed(2)}
                      &nbsp;=&nbsp; <strong className="text-foreground">RM {rowTotal.toFixed(2)}</strong>
                    </div>
                  </div>
                );
              })}

              <div className="flex justify-between text-sm font-semibold border-t border-border pt-3">
                <span>Total Claim</span>
                <span>RM {mileageTotal.toFixed(2)}</span>
              </div>
            </CardContent></Card>
          </>
        )}

        {isMileage && formAMode === 'subsistence' && (
          <>
            <label className="block">
              <span className="mb-1.5 block text-sm font-medium">Date</span>
              <input type="date" value={expenseDate} onChange={(e) => setExpenseDate(e.target.value)} className={fieldClass} />
            </label>

            <Card><CardContent className="space-y-4 p-4">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium">Subsistence items</p>
                <button type="button" onClick={() => setSubsistenceItems((p) => [...p, emptySubsistenceItem()])} className="flex items-center gap-1 text-xs text-primary hover:underline">
                  <Plus className="h-3.5 w-3.5" /> Add item
                </button>
              </div>

              {subsistenceItems.map((row, i) => {
                const lineTotal = row.unit * row.ratePerUnit;
                return (
                  <div key={i} className="space-y-2 rounded-xl border border-border p-3">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-semibold text-muted-foreground">Item {i + 1}</span>
                      {subsistenceItems.length > 1 && (
                        <button type="button" onClick={() => setSubsistenceItems((p) => p.filter((_, j) => j !== i))} className="text-destructive/70 hover:text-destructive">
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </div>
                    <input type="text" placeholder="Description (e.g. Buying water bottle)" value={row.description} onChange={(e) => updateSubsistenceItem(i, { description: e.target.value })} className={fieldClass} />
                    <div className="grid grid-cols-2 gap-2">
                      <label className="block">
                        <span className="mb-1 block text-xs text-muted-foreground">Unit</span>
                        <input type="number" step="1" min="0" value={row.unit || ''} onChange={(e) => updateSubsistenceItem(i, { unit: parseFloat(e.target.value) || 0 })} className={fieldClass} placeholder="0" />
                      </label>
                      <label className="block">
                        <span className="mb-1 block text-xs text-muted-foreground">RM / unit</span>
                        <input type="number" step="0.01" min="0" value={row.ratePerUnit || ''} onChange={(e) => updateSubsistenceItem(i, { ratePerUnit: parseFloat(e.target.value) || 0 })} className={fieldClass} placeholder="0.00" />
                      </label>
                    </div>
                    <div className="rounded-lg bg-muted/50 px-3 py-2 text-xs text-muted-foreground">
                      Total: <strong className="text-foreground">RM {lineTotal.toFixed(2)}</strong>
                    </div>
                  </div>
                );
              })}

              <div className="flex justify-between text-sm font-semibold border-t border-border pt-3">
                <span>Total Claim</span>
                <span>RM {subsistenceItemsTotal.toFixed(2)}</span>
              </div>
            </CardContent></Card>
          </>
        )}

        {/* ── SUBSISTENCE ─────────────────────────────────────────────── */}
        {isSubsistence && (
          <>
            <label className="block">
              <span className="mb-1.5 block text-sm font-medium">Purpose of travelling</span>
              <input type="text" value={purpose} onChange={(e) => setPurpose(e.target.value)} className={fieldClass} placeholder="e.g. HAZID Workshop, Alor Setar" required />
            </label>
            <label className="block">
              <span className="mb-1.5 block text-sm font-medium">Destination</span>
              <input type="text" value={destination} onChange={(e) => setDestination(e.target.value)} className={fieldClass} placeholder="e.g. RAIA Hotel, Alor Setar" />
            </label>
            <label className="block">
              <span className="mb-1.5 block text-sm font-medium">Travel date</span>
              <input type="date" value={expenseDate} onChange={(e) => setExpenseDate(e.target.value)} className={fieldClass} />
            </label>

            <Card><CardContent className="space-y-4 p-4">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium">Staff meal allowances</p>
                <button type="button" onClick={() => setSubRows((p) => [...p, emptySubRow()])} className="flex items-center gap-1 text-xs text-primary hover:underline">
                  <Plus className="h-3.5 w-3.5" /> Add staff
                </button>
              </div>
              <p className="text-xs text-muted-foreground">Rates: Breakfast RM10 · Lunch RM20 · Dinner RM20</p>

              {subRows.map((row, i) => {
                const total = row.breakfast + row.lunch + row.dinner + row.mileageAmount + row.entranceFee;
                return (
                  <div key={i} className="space-y-2 rounded-xl border border-border p-3">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-semibold text-muted-foreground">Staff {i + 1}</span>
                      {subRows.length > 1 && (
                        <button type="button" onClick={() => setSubRows((p) => p.filter((_, j) => j !== i))} className="text-destructive/70 hover:text-destructive">
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </div>
                    <input type="text" placeholder="Staff name" value={row.staffName} onChange={(e) => updateSubRow(i, { staffName: e.target.value })} className={fieldClass} />
                    <input type="text" placeholder="Day of travelling (e.g. Monday)" value={row.dayOfTravel} onChange={(e) => updateSubRow(i, { dayOfTravel: e.target.value })} className={fieldClass} />
                    <div className="grid grid-cols-3 gap-2">
                      <label className="block">
                        <span className="mb-1 block text-xs text-muted-foreground">Breakfast (RM)</span>
                        <input type="number" step="0.01" min="0" value={row.breakfast || ''} onChange={(e) => updateSubRow(i, { breakfast: parseFloat(e.target.value) || 0 })} className={fieldClass} placeholder="10.00" />
                      </label>
                      <label className="block">
                        <span className="mb-1 block text-xs text-muted-foreground">Lunch (RM)</span>
                        <input type="number" step="0.01" min="0" value={row.lunch || ''} onChange={(e) => updateSubRow(i, { lunch: parseFloat(e.target.value) || 0 })} className={fieldClass} placeholder="20.00" />
                      </label>
                      <label className="block">
                        <span className="mb-1 block text-xs text-muted-foreground">Dinner (RM)</span>
                        <input type="number" step="0.01" min="0" value={row.dinner || ''} onChange={(e) => updateSubRow(i, { dinner: parseFloat(e.target.value) || 0 })} className={fieldClass} placeholder="20.00" />
                      </label>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <label className="block">
                        <span className="mb-1 block text-xs text-muted-foreground">Mileage (RM)</span>
                        <input type="number" step="0.01" min="0" value={row.mileageAmount || ''} onChange={(e) => updateSubRow(i, { mileageAmount: parseFloat(e.target.value) || 0 })} className={fieldClass} placeholder="0.00" />
                      </label>
                      <label className="block">
                        <span className="mb-1 block text-xs text-muted-foreground">Entrance Fee (RM)</span>
                        <input type="number" step="0.01" min="0" value={row.entranceFee || ''} onChange={(e) => updateSubRow(i, { entranceFee: parseFloat(e.target.value) || 0 })} className={fieldClass} placeholder="0.00" />
                      </label>
                    </div>
                    <div className="rounded-lg bg-muted/50 px-3 py-2 text-xs text-muted-foreground">
                      Total allowance: <strong className="text-foreground">RM {total.toFixed(2)}</strong>
                    </div>
                  </div>
                );
              })}

              <div className="flex justify-between text-sm font-semibold border-t border-border pt-3">
                <span>Grand Total</span>
                <span>RM {subsistenceTotal.toFixed(2)}</span>
              </div>
            </CardContent></Card>
          </>
        )}

        {/* ── Generic forms ────────────────────────────────────────────── */}
        {!isMileage && !isSubsistence && (
          <>
            <label className="block">
              <span className="mb-1.5 block text-sm font-medium">Purpose</span>
              <input type="text" value={purpose} onChange={(e) => setPurpose(e.target.value)} className={fieldClass} placeholder="e.g. Business cards, client meeting transport" required />
            </label>

            <label className="block">
              <span className="mb-1.5 block text-sm font-medium">Date</span>
              <input type="date" value={expenseDate} onChange={(e) => setExpenseDate(e.target.value)} className={fieldClass} required />
            </label>


            {usesDirectClaimAmount ? (
              <label className="block">
                <span className="mb-1.5 block text-sm font-medium">Total claim (RM)</span>
                <div className="flex h-12 overflow-hidden rounded-xl border border-input bg-background shadow-sm focus-within:ring-2 focus-within:ring-ring">
                  <span className="flex items-center border-r border-input bg-muted px-4 text-sm font-semibold">RM</span>
                  <input
                    type="number"
                    inputMode="decimal"
                    step="0.01"
                    min="0.01"
                    value={claimAmount}
                    onChange={(e) => setClaimAmount(e.target.value)}
                    className="min-w-0 flex-1 bg-transparent px-4 text-base font-semibold outline-none"
                    placeholder="0.00"
                    required
                  />
                </div>
              </label>
            ) : (
              <Card><CardContent className="space-y-3 p-4">
                <div className="flex items-center justify-between">
                  <legend className="text-sm font-medium">Line items</legend>
                  <button type="button" onClick={() => setLines((p) => [...p, emptyLine()])} className="flex items-center gap-1 text-xs text-primary hover:underline">
                    <Plus className="h-3.5 w-3.5" /> Add line
                  </button>
                </div>
                {lines.map((line, index) => (
                  <div key={index} className="space-y-2 border-b border-border pb-3 last:border-0">
                    <div className="flex gap-2">
                      <input type="text" placeholder="Invoice no. (optional)" value={line.invoiceNo ?? ''} onChange={(e) => updateLine(index, { invoiceNo: e.target.value })} className={`${fieldClass} flex-1`} />
                      {lines.length > 1 && (
                        <button type="button" onClick={() => setLines((p) => p.filter((_, j) => j !== index))} className="text-destructive/70 hover:text-destructive">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                    <input type="text" placeholder="Description" value={line.description} onChange={(e) => updateLine(index, { description: e.target.value })} className={fieldClass} />
                    <div className="grid grid-cols-[minmax(0,1fr)_5rem] gap-2">
                      <label className="min-w-0">
                        <span className="mb-1 block text-xs text-muted-foreground">Amount (RM)</span>
                        <input type="number" inputMode="decimal" step="0.01" min="0" placeholder="0.00" value={line.amount || ''} onChange={(e) => updateLine(index, { amount: parseFloat(e.target.value) || 0 })} className={fieldClass} />
                      </label>
                      <label className="min-w-0">
                        <span className="mb-1 block text-xs text-muted-foreground">Qty</span>
                        <input type="number" inputMode="numeric" min="1" value={line.quantity ?? 1} onChange={(e) => updateLine(index, { quantity: parseInt(e.target.value, 10) || 1 })} className={fieldClass} />
                      </label>
                    </div>
                  </div>
                ))}
                <p className="text-sm font-semibold">Total: RM {genericTotal.toFixed(2)}</p>
              </CardContent></Card>
            )}

            <Card><CardContent className="space-y-3 p-4">
              <div className="flex items-center justify-between">
                <legend className="text-sm font-medium">Bank details</legend>
                {profile?.bankAccountName && (
                  <span className="text-xs text-primary">Pre-filled from profile</span>
                )}
              </div>
              <label className="block">
                <span className="mb-1 block text-xs text-muted-foreground">Payable to</span>
                <input type="text" placeholder="Account holder name" value={bankPayableTo} onChange={(e) => setBankPayableTo(e.target.value)} className={fieldClass} />
              </label>
              <label className="block">
                <span className="mb-1 block text-xs text-muted-foreground">Bank name</span>
                <input type="text" placeholder="e.g. Maybank" value={bankName} onChange={(e) => setBankName(e.target.value)} className={fieldClass} />
              </label>
              <label className="block">
                <span className="mb-1 block text-xs text-muted-foreground">Account number</span>
                <input type="text" placeholder="e.g. 1234 5678 9012" value={bankAccountNo} onChange={(e) => setBankAccountNo(e.target.value)} className={fieldClass} />
              </label>
            </CardContent></Card>

            {ref && (
              <Card><CardContent className="p-4">
                <legend className="mb-2 block text-sm font-medium">Documents attached</legend>
                <div className="space-y-2">
                  {Object.entries(ref.documentTypes).map(([code, label]) => (
                    <label key={code} className="flex items-center gap-2 text-sm text-foreground">
                      <input type="checkbox" checked={documents.includes(code)} onChange={() => toggleDocument(code)} className="rounded border-input text-primary focus:ring-ring" />
                      {label}
                    </label>
                  ))}
                </div>
              </CardContent></Card>
            )}
          </>
        )}

        {/* Proof documents — all types */}
        <Card><CardContent className="p-4 space-y-3">
          <p className="text-sm font-medium">Proof documents</p>
          <p className="text-xs text-muted-foreground">Attach receipts, invoices, or any supporting files.</p>
          <div className="flex gap-2">
            <Button type="button" variant="outline" size="sm" onClick={() => fileInputRef.current?.click()} disabled={uploadingFiles}>
              <Paperclip className="mr-1.5 h-4 w-4" />
              {uploadingFiles ? 'Uploading…' : 'Attach files'}
            </Button>
            <Button type="button" variant="outline" size="sm" onClick={() => setScannerOpen(true)} disabled={uploadingFiles}>
              <Camera className="mr-1.5 h-4 w-4" />
              Scan document
            </Button>
          </div>
          <input ref={fileInputRef} type="file" accept="image/*,application/pdf" multiple className="hidden" onChange={handleFileChange} />
          {proofFiles.length > 0 && (
            <ul className="space-y-2">
              {proofFiles.map((f, i) => (
                <li key={i} className="flex items-center gap-2 rounded-lg border border-border p-2 text-sm">
                  {f.url && /\.(jpg|jpeg|png|gif|webp)$/i.test(f.name) ? (
                    <img src={f.url} alt={f.name} className="h-10 w-10 rounded object-cover flex-shrink-0" />
                  ) : (
                    <div className="h-10 w-10 flex-shrink-0 rounded bg-muted flex items-center justify-center">
                      <Paperclip className="h-4 w-4 text-muted-foreground" />
                    </div>
                  )}
                  <span className="flex-1 truncate text-muted-foreground">{f.name}</span>
                  <button type="button" onClick={() => setProofFiles((p) => p.filter((_, j) => j !== i))} className="text-muted-foreground hover:text-destructive">
                    <X className="h-4 w-4" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </CardContent></Card>

        {/* Additional details — all types */}
        {!isSubsistence && (
          <label className="block">
            <span className="mb-1.5 block text-sm font-medium">Additional details</span>
            <textarea value={additionalDetails} onChange={(e) => setAdditionalDetails(e.target.value)} rows={3} className={fieldClass} />
          </label>
        )}

        {/* Summary */}
        <div className="rounded-xl border border-border bg-muted/40 px-4 py-3 flex items-center justify-between">
          <span className="text-sm font-medium">Total</span>
          <span className="text-base font-bold">RM {totalAmount.toFixed(2)}</span>
        </div>

        <div className="flex gap-3">
          <Button type="button" variant="outline" onClick={() => navigate('/claims')} className="flex-1">Cancel</Button>
          <Button type="button" variant="outline" onClick={handleSaveDraft} disabled={!companyEntityId || loading || uploadingFiles} className="flex-1">
            {loading ? 'Saving…' : 'Save draft'}
          </Button>
          <Button type="submit" disabled={!canSubmit} className="flex-1">
            {loading ? 'Submitting…' : 'Submit'}
          </Button>
        </div>
      </form>
    </>
  );
}

function readFileAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}
